/**
 * The report's "Your plan" page for D2C consumers: exactly the rows the Your Plan screen shows (from
 * the same model and formatter), then the applications they've sent with their terms as submitted,
 * kept apart from the live figures.
 */
import { Text, View } from '@react-pdf/renderer';
import { colors, onboardingPdfReportStyle } from '@/features/pdf-report/lib/pdf-theme';
import {
  YOUR_PLAN_LABELS,
  formatPlanGap,
  formatPlanMoney,
  formatPolicyCoverage,
  formatSubmittedOn,
} from '@/features/pdf-report/lib/your-plan-format';
import type { YourPlanReportModel } from '@/features/pdf-report/types';
import { CoinsIcon, FileTextIcon } from '../icons/pdf-icons';

const SCHEDULE_LABELS: Record<ConsumerPaymentSchedule, string> = {
  ongoing: 'Monthly, ongoing',
  tenPay: 'Annually for 10 years',
  singlePay: 'Pay once',
};

const row = {
  flexDirection: 'row' as const,
  justifyContent: 'space-between' as const,
  paddingVertical: 6,
  paddingHorizontal: 12,
  borderBottom: `0.75pt solid ${colors.border}`,
};
const label = { fontSize: 9, color: colors.foreground };
const value = { fontSize: 9, fontFamily: 'Helvetica-Bold', color: colors.gray950 };
const box = {
  border: `0.75pt solid ${colors.border}`,
  borderRadius: 8,
  marginBottom: 12,
  overflow: 'hidden' as const,
};

function Row({ name, amount, indent = false }: { name: string; amount: string; indent?: boolean }) {
  return (
    <View style={row}>
      <Text style={[label, indent ? { paddingLeft: 10 } : {}]}>{name}</Text>
      <Text style={value}>{amount}</Text>
    </View>
  );
}

export function YourPlanSection({ plan }: { plan: YourPlanReportModel }) {
  return (
    <View>
      <View style={onboardingPdfReportStyle.sectionHeader}>
        <View style={onboardingPdfReportStyle.sectionIconBox}>
          <CoinsIcon />
        </View>
        <Text style={onboardingPdfReportStyle.sectionTitle}>Your plan</Text>
      </View>
      <View style={box}>
        <Row
          name={YOUR_PLAN_LABELS.estimatedCost}
          amount={formatPlanMoney(plan.estimatedCost)}
        />
        {plan.selfFunding !== null && (
          <Row
            name={YOUR_PLAN_LABELS.selfFunding}
            amount={formatPlanMoney(plan.selfFunding)}
          />
        )}
        {plan.policyTotal !== null && (
          <Row
            name={YOUR_PLAN_LABELS.policyTotal}
            amount={formatPlanMoney(plan.policyTotal)}
          />
        )}
        {plan.policies.map(policy => (
          <Row
            key={policy.key}
            indent
            name={policy.statusLabel ? `${policy.name} (${policy.statusLabel})` : policy.name}
            amount={formatPolicyCoverage(policy.projectedCoverage)}
          />
        ))}
        <Row
          name={YOUR_PLAN_LABELS.combined}
          amount={formatPlanMoney(plan.combined)}
        />
        <Row
          name={YOUR_PLAN_LABELS.gap}
          amount={formatPlanGap(plan.gap)}
        />
      </View>

      {plan.applications.length > 0 && (
        <View>
          <View style={onboardingPdfReportStyle.sectionHeader}>
            <View style={onboardingPdfReportStyle.sectionIconBox}>
              <FileTextIcon />
            </View>
            <Text style={onboardingPdfReportStyle.sectionTitle}>Applications you’ve sent</Text>
          </View>
          {plan.applications.map(({ id, submittedAt, coverage, inFundingPlan }) => (
            <View
              key={id}
              style={box}
            >
              <Row
                name={`${coverage.carrierName} · ${coverage.productName}`}
                amount="Application submitted"
              />
              <Row
                name="Sent"
                amount={formatSubmittedOn(submittedAt)}
              />
              <Row
                name="Premium"
                amount={`${formatPlanMoney(coverage.premium)} · ${SCHEDULE_LABELS[coverage.paymentSchedule]}`}
              />
              <Row
                name="Projected policy payout, as submitted"
                amount={formatPlanMoney(coverage.projectedPayout)}
              />
              <Row
                name="Left for you to cover, as submitted"
                amount={formatPlanMoney(coverage.outOfPocket)}
              />
              {!inFundingPlan && (
                <View style={{ paddingVertical: 6, paddingHorizontal: 12 }}>
                  <Text style={label}>
                    Not counted in your plan. Your application is still with our team.
                  </Text>
                </View>
              )}
            </View>
          ))}
          <Text style={{ fontSize: 8, color: colors.mutedFg }}>
            Coverage isn’t active until the insurance company approves the application and the
            policy takes effect.
          </Text>
        </View>
      )}
    </View>
  );
}
